import { DOCUMENT, isPlatformBrowser, NgClass } from '@angular/common';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, Input, PLATFORM_ID, signal } from '@angular/core';
import { asyncScheduler, fromEvent, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil, throttleTime } from 'rxjs/operators';

import { ButtonV3Component } from 'components/atoms/button-v3/button-v3.component';
import type { IButtonStyleProperties } from 'components/atoms/button-v3/entities/types';
import type { IImageConfig } from 'components/atoms/image/entities/types';
import { ImageComponent } from 'components/atoms/image/image.component';
import { imageConfig } from 'components/molecules/pinned-cta/entities/const';
import type { IPinnedCtaData, TPinnedCTACallToAction } from 'components/molecules/pinned-cta/entities/types';
import { EIconType } from 'components/molecules/trial-form-v2/entities/types';
import { HeaderV2Service } from 'components/organisms/header-v2/service/header-v2.service';
import type { ModalControl } from 'components/organisms/modal';
import { WidgetsService } from 'components/organisms/widgets-loader/widgets.service';
import { StickyDirective } from 'src/directives/sticky/sticky.directive';
import { ThemeDirective } from 'src/directives/theme/theme.directive';
import { ButtonV3Service } from 'src/services/button/button-v3.service';
import { CallToActionService } from 'src/services/call-to-action/call-to-action.service';
import { LocaleService } from 'src/services/locale/locale.service';
import type { IPromoInstrumentEvent } from 'src/services/promo-instruments/promo-instruments.service';
import { PromoInstrumentService } from 'src/services/promo-instruments/promo-instruments.service';
import { ThemeService } from 'src/services/theme/theme.service';
import { INNER_WIDTH_DESKTOP_SMALL, INNER_WIDTH_TABLET } from 'src/shared/constants/breakpoints/breakpoints';
import type { TFullFormOptions, TrialPopupForm } from 'src/shared/types/call-to-action-item';
import type { TFullColorScheme } from 'src/shared/types/color-schemes-and-themes';
import { EStickySlot } from 'src/shared/types/sticky';

@Component({
    selector: 'app-pinned-cta',
    templateUrl: './pinned-cta.component.html',
    styleUrls: ['./pinned-cta.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, ImageComponent, ButtonV3Component, ThemeDirective, StickyDirective],
})
export class PinnedCtaComponent implements OnInit, OnDestroy, OnChanges {
    private callToActionService = inject(CallToActionService);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly localeService = inject(LocaleService);
    private readonly widgetsService = inject(WidgetsService);
    private readonly buttonV3Service = inject(ButtonV3Service);
    private readonly headerService = inject(HeaderV2Service);
    readonly themeService = inject(ThemeService);
    private readonly promoInstrumentService = inject(PromoInstrumentService);

    @Input() pinnedCtaData: IPinnedCtaData;

    private readonly onDestroy$$ = new Subject();
    private modalControl: ModalControl;
    public readonly isCtaHidden = signal(true);
    public readonly isStickyActive = signal(false);
    public readonly stickySlot = EStickySlot.Header;
    public callToActionItem: TPinnedCTACallToAction;
    public buttonStyleProperties = signal<IButtonStyleProperties>({});
    public readonly HEIGHT_OF_FAKE_HEADER = 52;
    public readonly imageConfig: IImageConfig = imageConfig;
    public readonly pathToRootLocalHomePage = this.localeService.getPathToLocaleSlug() + '/';
    public colorScheme: TFullColorScheme = 'white';
    public theme: 'dark' | 'light' = 'light';

    private get fullFormOptions(): TFullFormOptions {
        return this.callToActionService.getFullFormOptions(this.callToActionItem);
    }

    public ngOnInit(): void {
        this.setPinnedData();

        if (this.callToActionItem) {
            if (isPlatformBrowser(this.platformId)) {
                this.setIsCtaHidden();
                this.setButtonSize();

                this.headerService.state$
                    .pipe(
                        takeUntil(this.onDestroy$$),
                        map((state) => state.isMobileMenuOpen),
                        distinctUntilChanged()
                    )
                    .subscribe(() => {
                        this.setIsCtaHidden();
                    });
            }
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.pinnedCtaData?.currentValue) {
            this.setPinnedData();
            this.subscribeToPromoInstrumentEvents();
        }
    }

    public ngOnDestroy(): void {
        this.onDestroy$$.next(null);
        this.onDestroy$$.complete();
    }

    public openFormPopup(): void {
        const disclaimerOptions = (this.callToActionItem as TrialPopupForm)?.disclaimerOptions || {
            text: '',
            isWithIcon: true,
            iconType: EIconType.Wrike,
        };
        this.modalControl = this.callToActionService.onOpenPopup(
            this.callToActionItem?.content,
            this.fullFormOptions,
            disclaimerOptions,
            this.colorScheme
        );
    }

    public setIsCtaHidden(): void {
        if (isPlatformBrowser(this.platformId)) {
            let secondaryMenu: HTMLElement | null;

            this.widgetsService.waitForWidgets().then(() => {
                secondaryMenu = this.document.querySelector('.widget-secondary-menu')?.querySelector('.menu');
                this.isCtaHidden.set(this.isFirstViewport() || this.headerService.state.isMobileMenuOpen);
                this.updateStickyState(window.innerWidth);
            });

            fromEvent(window, 'scroll', { passive: true })
                .pipe(takeUntil(this.onDestroy$$), throttleTime(50, asyncScheduler, { leading: true, trailing: true }))
                .subscribe(() => {
                    this.isCtaHidden.set(this.isFirstViewport() || this.headerService.state.isMobileMenuOpen);
                    this.updateStickyState(window.innerWidth);

                    if (secondaryMenu && !this.pinnedCtaData.isHeaderExisted) {
                        const isPinnedCtaHeaderActive = !this.isCtaHidden() && window.innerWidth < INNER_WIDTH_TABLET;
                        this.headerService.setPinnedCtaHeaderActive(isPinnedCtaHeaderActive);

                        if (isPinnedCtaHeaderActive) {
                            this.headerService.heightSubject.next(this.HEIGHT_OF_FAKE_HEADER);
                        } else {
                            this.headerService.heightSubject.next(0);
                        }
                    }

                    this.setButtonSize();
                });
        }
    }

    private updateStickyState(innerWidth: number): void {
        const isMobile = innerWidth < INNER_WIDTH_TABLET;
        const isHiddenByHeader = this.pinnedCtaData?.isHeaderExisted;
        const isInitialPosition = this.pinnedCtaData?.isBannerExisted;

        this.isStickyActive.set(!this.isCtaHidden() && isMobile && !isHiddenByHeader && !isInitialPosition);
    }

    private setPinnedData() {
        this.colorScheme = this.pinnedCtaData.theme as TFullColorScheme;
        this.theme = this.pinnedCtaData.theme === 'white' ? 'light' : 'dark';
        this.callToActionItem = this.pinnedCtaData?.callToActions?.[0];

        if (this.callToActionItem) {
            this.buttonStyleProperties.set(
                this.buttonV3Service.getCallToActionButtonStyleProperties(
                    this.callToActionItem.buttonOptions,
                    this.colorScheme
                )
            );
        }
    }

    public setButtonSize() {
        this.buttonStyleProperties.set({
            ...this.buttonStyleProperties(),
            size:
                window.innerWidth < INNER_WIDTH_TABLET
                    ? 'extra-small'
                    : window.innerWidth < INNER_WIDTH_DESKTOP_SMALL
                      ? 'small'
                      : 'large',
        });
    }

    private isFirstViewport(): boolean {
        const scrollPosition = window.scrollY;
        const viewportHeight = window.innerHeight;
        return scrollPosition < viewportHeight;
    }

    private subscribeToPromoInstrumentEvents(): void {
        if (!this.pinnedCtaData?.id) return;

        this.promoInstrumentService.observePromoInstrument(this.pinnedCtaData.id, (params: IPromoInstrumentEvent) => {
            if (params) {
                if (params.data?.layout) {
                    this.pinnedCtaData = {
                        ...this.pinnedCtaData,
                        ...(params.data.layout.pinned_button as IPinnedCtaData),
                    };
                    this.setPinnedData();
                }
                this.modalControl?.close();
                this.isCtaHidden.set(false); // Force CTA to be visible for admin panel live editor
                this.openFormPopup();
            } else {
                this.modalControl?.close();
            }
        });
    }
}
